import { useState, useEffect, useCallback } from 'react';
import { authApi, tokenStore, type AuthResponse } from '../lib/api';

export interface AuthState {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: { id: string; email: string } | null;
  orgs: Array<{ id: string; role: string }>;
}

export function useAuth() {
  const [state, setState] = useState<AuthState>({
    isAuthenticated: false,
    isLoading: true,
    user: null,
    orgs: [],
  });

  const loadUser = useCallback(async () => {
    if (!tokenStore.getAccess()) {
      setState({ isAuthenticated: false, isLoading: false, user: null, orgs: [] });
      return;
    }
    try {
      const me = await authApi.me();
      setState({ isAuthenticated: true, isLoading: false, user: { id: me.id, email: me.email }, orgs: me.orgs });
    } catch {
      tokenStore.clear();
      setState({ isAuthenticated: false, isLoading: false, user: null, orgs: [] });
    }
  }, []);

  useEffect(() => { loadUser(); }, [loadUser]);

  const login = async (email: string, password: string): Promise<AuthResponse> => {
    const r = await authApi.login(email, password);
    await loadUser();
    return r;
  };

  const register = async (email: string, password: string, org_name?: string): Promise<AuthResponse> => {
    const r = await authApi.register(email, password, org_name);
    tokenStore.set(r.access_token, r.refresh_token);
    await loadUser();
    return r;
  };

  const logout = async () => {
    await authApi.logout().catch(() => {});
    setState({ isAuthenticated: false, isLoading: false, user: null, orgs: [] });
  };

  return { ...state, login, register, logout };
}
